import type { RunSummary, RunsIndex } from './types';
import { formatDistance, formatDuration, formatPace, formatShortDate, typeLabel } from './format';
import { Carousel, type CarouselItem } from './Carousel';
import { config } from '../config';

interface Props {
  runs: RunSummary[];
  totals: RunsIndex['totals'];
  hoveredId: string | null;
  onHover: (id: string | null) => void;
  onSelect: (id: string) => void;
  onPlay?: (id: string) => void;
}

export function RunList({ runs, totals, hoveredId, onHover, onSelect, onPlay }: Props) {
  const unit = config.distanceUnit;

  const items: CarouselItem[] = runs.map((run) => ({
    key: run.id,
    label: run.name,
    render: () => (
      // On mobile (carousel panel) the card centers vertically in the panel.
      // On desktop (scroll list) it just sits in the list with its own chrome.
      <div className="h-full flex items-center md:block">
        <RunCard
          run={run}
          unit={unit}
          isHovered={hoveredId === run.id}
          onHoverEnter={() => onHover(run.id)}
          onHoverLeave={() => onHover(null)}
          onClick={() => onSelect(run.id)}
          onPlay={onPlay ? () => onPlay(run.id) : undefined}
        />
      </div>
    )
  }));

  return (
    <div className="h-full flex flex-col min-h-0">
      <header className="px-5 pt-5 pb-3 md:px-6 md:pt-6 md:pb-4 border-b border-ink-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-accent" />
            <span className="text-[11px] uppercase tracking-[0.18em] text-ink-500 font-medium">
              All Runs
            </span>
          </div>
          <span className="text-[11px] text-ink-500 tracking-wide font-mono">{runs.length}</span>
        </div>
        <h2 className="font-display text-[22px] md:text-[26px] leading-tight text-ink-900 mt-1.5">
          {formatDistance(totals.distance_m, unit)} · {formatDuration(totals.duration_s)}
        </h2>
      </header>

      <Carousel
        items={items}
        desktopMode="scroll-list"
        resetKey={runs.length}
        onActiveChange={(_, item) => onHover(item.key)}
      />
    </div>
  );
}

interface CardProps {
  run: RunSummary;
  unit: 'mi' | 'km';
  isHovered: boolean;
  onHoverEnter: () => void;
  onHoverLeave: () => void;
  onClick: () => void;
  onPlay?: () => void;
}

function RunCard({ run, unit, isHovered, onHoverEnter, onHoverLeave, onClick, onPlay }: CardProps) {
  return (
    <div
      className="relative w-full"
      onMouseEnter={onHoverEnter}
      onMouseLeave={onHoverLeave}
    >
      <button
        type="button"
        onFocus={onHoverEnter}
        onBlur={onHoverLeave}
        onClick={onClick}
        className={[
          'w-full text-left',
          'rounded-2xl border border-ink-100 bg-white/70 hover:bg-white',
          'shadow-chip hover:shadow-panel',
          'px-4 py-3.5 md:px-4 md:py-3.5 transition-all duration-200',
          'hover:-translate-y-0.5',
          isHovered ? 'ring-2 ring-accent/60' : ''
        ].join(' ')}
      >
        <div className="flex items-center justify-between gap-2 mb-1.5">
          <div className="flex items-center gap-2 min-w-0">
            <span className={`pill ${typePillClasses(run.type)}`}>{typeLabel(run.type)}</span>
            <span className="text-[11px] text-ink-500 tracking-wide truncate">
              {formatShortDate(run.date)}
            </span>
          </div>
          <ChevronRight
            className={`shrink-0 text-ink-300 transition-transform duration-200 ${
              isHovered ? 'translate-x-0.5 text-ink-500' : ''
            }`}
          />
        </div>
        <div className="font-display text-[18px] md:text-[18px] leading-tight text-ink-900 truncate mb-2.5">
          {run.name}
        </div>
        <div className="grid grid-cols-3 gap-2 pr-10">
          <MiniStat label="Dist" value={formatDistance(run.distance_m, unit)} />
          <MiniStat label="Pace" value={formatPace(run.avg_pace_s_per_km, unit).replace(` /${unit}`, '')} />
          <MiniStat label="Time" value={formatDuration(run.duration_s)} />
        </div>
      </button>
      {onPlay && (
        <button
          type="button"
          onClick={onPlay}
          onMouseEnter={onHoverEnter}
          onFocus={onHoverEnter}
          className="absolute bottom-3 right-3 w-8 h-8 rounded-full bg-accent text-white flex items-center justify-center shadow-sm hover:brightness-110 transition"
          aria-label={`Play flyby of ${run.name}`}
        >
          <svg width="10" height="10" viewBox="0 0 12 12" fill="currentColor" aria-hidden>
            <path d="M2.5 1.5 L10 6 L2.5 10.5 Z" />
          </svg>
        </button>
      )}
    </div>
  );
}

function MiniStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <div className="text-[9px] uppercase tracking-[0.14em] text-ink-500 font-medium">{label}</div>
      <div className="mt-0.5 font-mono text-[12px] text-ink-900 truncate">{value}</div>
    </div>
  );
}

function ChevronRight({ className = '' }: { className?: string }) {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden className={className}>
      <path
        d="M5 2.5L9.5 7L5 11.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function typePillClasses(type: string): string {
  switch (type) {
    case 'tempo':
      return 'bg-accent-soft text-accent';
    case 'race':
      return 'bg-ink-900 text-white';
    case 'long':
      return 'bg-ink-100 text-ink-700';
    case 'interval':
      return 'bg-amber-100 text-amber-800';
    case 'recovery':
      return 'bg-emerald-100 text-emerald-800';
    case 'easy':
      return 'bg-sky-100 text-sky-800';
    default:
      return 'bg-ink-100 text-ink-700';
  }
}
